import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveTopicJob, clearActiveTopicJob } from '../store/slices/analyticsSlice';
import * as analyticsApi from '../api/analytics';

/**
 * Non-blocking floating panel showing topic clustering or embedding progress.
 * Persists across page navigation via Redux. Mirrors SyncProgressPanel pattern.
 */
function TopicProgressPanel() {
  const dispatch = useDispatch();
  const job = useSelector((state) => state.analytics.activeTopicJob);
  const hasSyncPanel = useSelector((state) =>
    !!(state.analytics.syncConfig.activeWorkflow || state.analytics.activeSyncWorkflow)
  );
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const timerRef = useRef(null);

  // Reset dismissed/minimized state when a new job starts
  useEffect(() => {
    if (job?.status === 'running') {
      setIsDismissed(false);
      setIsMinimized(false);
    }
  }, [job?.sessionId, job?.configId, job?.type]);

  // Elapsed time ticker
  useEffect(() => {
    if (!job || job.status !== 'running') {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    const startMs = new Date(job.startedAt).getTime();
    const tick = () => setElapsedSec(Math.floor((Date.now() - startMs) / 1000));
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [job?.startedAt, job?.status]);

  // Poll for completion
  useEffect(() => {
    if (!job || job.status !== 'running') return;

    const poll = async () => {
      try {
        if (job.type === 'clustering' && job.sessionId) {
          const res = await analyticsApi.fetchTopicSession(job.sessionId);
          const data = res.data || res;
          if (data.clusters && data.clusters.length > 0) {
            dispatch(setActiveTopicJob({
              ...job,
              status: 'completed',
              clusterCount: data.clusters.length,
            }));
          }
        } else if (job.type === 'embedding' && job.configId) {
          const res = await analyticsApi.fetchTopicEmbeddingsStatus(job.configId);
          const data = res.data || res;
          if (data.status === 'ready') {
            dispatch(setActiveTopicJob({
              ...job,
              status: 'completed',
              recordCount: data.record_count,
            }));
          }
        }
      } catch (_) {
        // Non-critical — keep polling
      }
    };

    poll();
    const interval = setInterval(poll, 4000);
    return () => clearInterval(interval);
  }, [job?.status, job?.type, job?.sessionId, job?.configId, dispatch]);

  // Auto-dismiss after completion
  useEffect(() => {
    if (job?.status === 'completed') {
      const t = setTimeout(() => handleDismiss(), 8000);
      return () => clearTimeout(t);
    }
  }, [job?.status]);

  const handleDismiss = () => {
    setIsDismissed(true);
    dispatch(clearActiveTopicJob()); // also clears localStorage
  };

  if (!job || isDismissed) return null;

  // Stack above SyncProgressPanel if it's also visible
  const bottomClass = hasSyncPanel ? 'bottom-24' : 'bottom-4';

  const isRunning = job.status === 'running';
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';

  const getTitle = () => {
    if (job.type === 'clustering') {
      if (isCompleted) return 'Topics Ready';
      if (isFailed) return 'Clustering Failed';
      return 'Generating Topics';
    }
    if (isCompleted) return 'Embeddings Ready';
    if (isFailed) return 'Embedding Failed';
    return 'Preparing Embeddings';
  };

  const getEstimate = () => {
    if (job.type === 'clustering') return 'Usually 1–3 minutes';
    return 'Usually 2–5 minutes';
  };

  const formatElapsed = (sec) => {
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  };

  const headerColor = isCompleted ? 'bg-green-600' : isFailed ? 'bg-red-600' : 'bg-violet-600';

  if (isMinimized) {
    return (
      <div className={`fixed ${bottomClass} right-4 z-[49]`}>
        <button
          onClick={() => setIsMinimized(false)}
          className={`${headerColor} text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 hover:opacity-90 transition`}
        >
          {isRunning && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {isCompleted && (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          <span className="text-sm font-medium">{getTitle()}</span>
          {isRunning && <span className="text-xs opacity-75">{formatElapsed(elapsedSec)}</span>}
        </button>
      </div>
    );
  }

  return (
    <div className={`fixed ${bottomClass} right-4 z-[49] w-72 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden`}>
      {/* Header */}
      <div className={`${headerColor} px-4 py-2 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          {isRunning && (
            <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          <span className="text-white font-medium text-sm">{getTitle()}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(true)}
            className="text-white/80 hover:text-white p-1"
            title="Minimize"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          {!isRunning && (
            <button onClick={handleDismiss} className="text-white/80 hover:text-white p-1" title="Dismiss">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        {isRunning && (
          <>
            <p className="text-sm font-medium text-gray-800 truncate mb-1">{job.configName}</p>
            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
              <span>{getEstimate()}</span>
              <span className="font-mono">{formatElapsed(elapsedSec)} elapsed</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-violet-500 rounded-full transition-all duration-1000"
                style={{
                  width: `${Math.min(95, (elapsedSec / (job.type === 'clustering' ? 120 : 240)) * 100)}%`,
                }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-2">You can navigate away — we'll notify you here when done.</p>
          </>
        )}

        {isCompleted && (
          <div className="text-center">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-800">{job.configName}</p>
            {job.type === 'clustering' && job.clusterCount > 0 && (
              <p className="text-2xl font-bold text-violet-600 mt-1">{job.clusterCount} topics</p>
            )}
            {job.type === 'embedding' && job.recordCount > 0 && (
              <p className="text-sm text-gray-500 mt-1">{job.recordCount.toLocaleString()} records embedded</p>
            )}
            <p className="text-xs text-gray-400 mt-1">Open Topics to explore results</p>
          </div>
        )}

        {isFailed && (
          <div className="text-center">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-sm text-red-600">{job.error || 'Something went wrong'}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TopicProgressPanel;
